/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Ronnel Bedana - ronnel@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Injectable, EventEmitter } from '@angular/core';
import { Field, Helpers, Query, Filter, Display, Order, Operand, Stage } from '../model';
import { BehaviorSubject } from 'rxjs';
import { ReportingProvider, ReportProvider } from '../providers';
import { ModeService } from './mode.service';
import { SystemType } from '../model/enums/system.type';
import { ReportingType } from '../model/enums/reporting.type';
import { QueryType } from '../model/enums/query.type';
import { OrderDirection } from '../model/field';
import { LogService } from 'src/app/services';

@Injectable()
export class ReportingService {

  onSearchField: EventEmitter<string> = new EventEmitter();
  onManage: EventEmitter<void> = new EventEmitter();

  currentMode: SystemType;
  currentReportingType: ReportingType;
  fromDate: string;
  toDate: string;

  private _filterFields: Filter[] = [];
  private _displayFields: Display[] = [];
  private _orderFields: Order[] = [];
  private _nameFields: String[] = [];

  private allFields: Display[] = this.getFields().map(field => {
    return {
      KEY: field.KEY,
    };
  });

  private nameFields: String[] = this.getFields().map(field => field.NAME);

  private filterFields = new BehaviorSubject<Filter[]>(this._filterFields);
  filterFields$ = this.filterFields.asObservable();

  private displayFields = new BehaviorSubject<Display[]>(this._displayFields);
  displayFields$ = this.displayFields.asObservable();

  private orderFields = new BehaviorSubject<Order[]>(this._orderFields);
  orderFields$ = this.orderFields.asObservable();

  private result = new BehaviorSubject<any>([]);
  result$ = this.result.asObservable();

  isChanged = new BehaviorSubject<boolean>(false);
  isChanged$ = this.isChanged.asObservable();

  constructor(
    private reportingProvider: ReportingProvider,
    private modeService: ModeService,
    private reportProvider: ReportProvider,
    public logService : LogService
  ) {
    this.modeService.mode$.subscribe(mode => this.currentMode = mode);
  }

  init(): void {
    this.changeResult(this.getQuery(), 1,this.fromDate,this.toDate);
    this.changeDisplayFields(this.getDefaultFields(), true);
  }

  refresh(): void {
    this.changeResult(this.getQuery(), 1);
  }

  getFields(): Field[] {
    return Helpers.getFields(this.currentReportingType);
  }

  getDefaultFields(): Field[] {
    return Helpers.getDefaultFields(this.currentReportingType);
  }

  changeFilterFields(fields: Filter[]): void {
    this._filterFields = fields;
    this.filterFields.next(this._filterFields);
    this.changeResult(this.getQuery(), 1);
  }

  changeDisplayFields(fields: Display[], doNotCallAPI?: boolean): void {
    this._displayFields = fields;
    this.displayFields.next(fields);
    if(!doNotCallAPI) {
      this.changeResult(this.getQuery(), 1, this.fromDate, this.toDate);
    }
  }

  changeOrderFields(fields: Order[]): void {
    this._orderFields = fields;
    this.orderFields.next(this._orderFields);
    this.changeResult(this.getQuery(), 1);
  }

  loadMore(page: number): void {
    this.changeResult(this.getQuery(), page);
  }

  getQuery(): Query {
    // Extract ID from VALUE objects for API payload
    const filters = this.filterFields.getValue().map(filter => {
      if (filter.VALUE && typeof filter.VALUE === 'object' && filter.VALUE !== null && (filter.VALUE as any).ID) {
        return {
          ...filter,
          VALUE: (filter.VALUE as any).ID as string
        };
      }
      return filter;
    });

    return {
      FILTER: filters,
      DISPLAY: this.displayFields.getValue(),
      ORDER: this.orderFields.getValue(),
      NAME: this.displayFields.getValue().map(field => field.NAME)
    };
  }

  changeResult(query: Query, page?: number, fromDate?: string, toDate?: string,order?:string , sort:boolean = false,size: number=10,sortBy?:string): void {
    if (this.currentReportingType == ReportingType.ORDERS) {
      this.reportingProvider.getReports(query, this.currentMode, page)
        .subscribe(data => {
          this.result.next(data);
        }, err => {
          this.logService.error(err);
        });
    }
    else if (this.currentReportingType == ReportingType.QUOTES) {
      this.reportingProvider.getQuotes(query, this.currentMode, page)
        .subscribe(data => {
          this.result.next(data);
        }, err => {
          this.logService.error(err);
        });
    }
    else if (this.currentReportingType == ReportingType.ERRORS) {
      let stage = Stage.parseArray([{ ID: "SBMT" }, { ID: "SUBR" }])
      this.reportProvider.getFilteredReports(page, 10, true, 'CREATED_DATE', this.currentMode, QueryType.ALL, null, null, stage)
        .subscribe(data => {
          this.result.next(data);
        }, err => {
          this.logService.error(err);
        });
    }
    else if (this.currentReportingType == ReportingType.TIER_RATING) {
      let orderBy = "", sort = false;
      if (query.ORDER.length > 0) {
        orderBy = query.ORDER[0].KEY;
        sort = query.ORDER[0].VALUE == OrderDirection.ASC ? true : false;
      }
      this.reportingProvider.getReportAnalystTierErrors(query, this.currentMode, fromDate, toDate, orderBy, page, 10, sort)
        .subscribe(data => {
          this.result.next(data);
        }, err => {
          this.logService.error(err);
        });
    }

    else if (this.currentReportingType == ReportingType.DROP_OFF) {
      this.reportingProvider.getDropOffReport(query,this.currentMode,page,size, sort,order, fromDate,toDate,sortBy)
      .subscribe(data => {
        this.result.next(data);
      }, err => {
        this.logService.error(err);
      });

    }
  }

  changePreset(query: Query): void {
    this._filterFields = query.FILTER.map(field => {
      const _selected = this.getFields().filter(col => col.KEY === `${field.KEY}`)[0];
      return {
        KEY: field.KEY,
        NAME: _selected.NAME,
        VALUE: field.VALUE,
        OPERAND: field.OPERAND
      };
    });

    this._displayFields = query.DISPLAY.map(field => {
      const _selected = this.getFields().filter(col => col.KEY === `${field.KEY}`)[0];
      return {
        KEY: field.KEY,
        NAME: _selected.NAME
      };
    });

    this._orderFields = query.ORDER.map(field => {
      const _selected = this.getFields().filter(col => col.KEY === `${field.KEY}`)[0];
      return {
        KEY: field.KEY,
        NAME: _selected.NAME,
        VALUE: field.VALUE
      };
    });

    this.filterFields.next(this._filterFields);
    this.displayFields.next(this._displayFields);
    this.orderFields.next(this._orderFields);
    this.changeResult(this.getQuery(), 1);
  }


  getDisplay(): Display[] {
    return this._displayFields;
  }

  getFilter(): Filter[] {
    return this._filterFields;
  }

  getOrder(): Order[] {
    return this._orderFields;
  }

  clearPreset(): void {
    this._filterFields = [];
    this._displayFields = this.getDefaultFields()
    this._orderFields = [];

    this.filterFields.next(this._filterFields);
    this.displayFields.next(this._displayFields);
    this.orderFields.next(this._orderFields);
    this.changeResult(this.getQuery(), 1);
  }
}
